import React from 'react';
import { Rocket, Server, Code, Layout, TerminalSquare, Globe, ShoppingCart, BarChart3, Layers, Gamepad2, Smartphone } from "lucide-react";
import { Template } from '../types/template';

/**
 * Icon mapping for template categories and specific templates
 */
export const getTemplateIcon = (template: Template | { icon?: string; category?: string }): React.ReactElement => {
  // First check if template has a specific icon
  if (template.icon) {
    switch (template.icon) {
      case '🚀':
      case 'rocket':
        return <Rocket />;
      case '⚡':
      case 'server':
        return <Server />;
      case '🌐':
      case 'globe':
        return <Globe />;
      case 'shopping-cart':
        return <ShoppingCart />;
      case 'bar-chart':
        return <BarChart3 />;
      case 'layers':
        return <Layers />;
      case 'gamepad':
        return <Gamepad2 />;
      case 'layout':
        return <Layout />;
      case '⚙️':
      case 'terminal':
        return <TerminalSquare />;
      case '⚛️':
      case 'code':
        return <Code />;
      case 'smartphone':
        return <Smartphone />;
      case '🟢':
        return <Server />;
      default:
        // If icon is an emoji, return it as text
        if (template.icon.length <= 2) {
          return <span style={{ fontSize: '1.2em' }}>{template.icon}</span>;
        }
    }
  }

  // Fallback to category-based icons
  switch (template.category) {
    case 'web':
      return <Rocket />;
    case 'backend':
      return <Server />;
    case 'frontend':
      return <Code />;
    case 'tools':
      return <TerminalSquare />;
    case 'general':
    default:
      return <Layout />;
  }
};



/**
 * Filter templates by category
 */
export const filterTemplatesByCategory = (templates: Template[], category: string): Template[] => {
  return templates.filter(template => template.category === category);
};

/**
 * Filter active templates only
 */
export const getActiveTemplates = (templates: Template[]): Template[] => {
  return templates.filter(template => template.is_active);
};

/**
 * Get unique categories from templates
 */
export const getUniqueCategories = (templates: Template[]): string[] => {
  const categories = templates.map(template => template.category);
  return Array.from(new Set(categories)).sort();
};

/**
 * Sort templates by name
 */
export const sortTemplatesByName = (templates: Template[]): Template[] => {
  return [...templates].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Sort templates by category, then by name
 */
export const sortTemplatesByCategoryAndName = (templates: Template[]): Template[] => {
  return [...templates].sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return a.name.localeCompare(b.name);
  });
};

/**
 * Find template by ID
 */
export const findTemplateById = (templates: Template[], id: string): Template | undefined => {
  return templates.find(template => template.id === id);
};

/**
 * Search templates by name or description
 */
export const searchTemplates = (templates: Template[], query: string): Template[] => {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return templates;

  return templates.filter(template => 
    template.name.toLowerCase().includes(normalizedQuery) ||
    template.description.toLowerCase().includes(normalizedQuery) ||
    template.tags.some(tag => tag.toLowerCase().includes(normalizedQuery))
  );
}; 